import type { PrimerQuestion, PrimerQuestionConfig } from './api-types';

/** Filename for externally hosted distributable SDL (web root). */
export const DISTRIBUTABLE_SDL_FILENAME = 'sdl.json';

export interface SDLPayload {
  brand: string;
  industry: string;
  last_updated: string;
  competitors: unknown[];
  strategic_foci?: any;
  variables?: any;
  questions: Array<{
    id: string;
    text: string;
    category: string;
    subcategory: string;
    priority: number;
    tags: string[];
    reference_answer: string;
    ideal_mentions: string[];
    competing_brands_context: string[];
    question_stage?: string;
    template_number?: number | null;
    template?: string;
    q_type?: string;
    framing_modifiers?: string[];
    answer_mode?: string;
    type_specific_checks?: string[];
    scoring_notes?: string;
  }>;
}

function mapQuestionToSDL(q: PrimerQuestion) {
  return {
    id: q.id,
    text: q.question,
    category: q.context_type || q.labels?.[0]?.name || '',
    subcategory: q.subcategory || '',
    priority: q.priority ?? q.order_index,
    tags: q.tags?.length ? q.tags : (q.labels?.map(l => l.name) || []),
    reference_answer: q.answer,
    ideal_mentions: q.ideal_mentions || [],
    competing_brands_context: q.competing_brands_context || [],
    question_stage: q.question_stage || '',
    template_number: q.template_number || null,
    template: q.template || '',
    q_type: q.q_type || '',
    framing_modifiers: q.framing_modifiers || [],
    answer_mode: q.answer_mode || '',
    type_specific_checks: q.type_specific_checks || [],
    scoring_notes: q.scoring_notes || '',
  };
}

export function resolveSDLBrandName(
  tenant: { name?: string; subdomain?: string } | null | undefined,
  fallbackSubdomain?: string | null,
): string {
  const name = tenant?.name?.trim();
  if (name) return name;
  const subdomain = tenant?.subdomain?.trim() || fallbackSubdomain?.trim();
  return subdomain || '';
}

/** SDL JSON for questions marked “Include In LARS” (is_optimizable). */
export function buildOptimizableSDLPayload(questions: PrimerQuestion[], brandName: string, config?: PrimerQuestionConfig | null): SDLPayload {
  const optimizable = questions.filter(q => q.is_optimizable);
  return {
    brand: brandName,
    industry: '',
    last_updated: new Date().toISOString().split('T')[0],
    competitors: [],
    ...(config?.strategic_foci ? { strategic_foci: config.strategic_foci } : {}),
    ...(config?.variables ? { variables: config.variables } : {}),
    questions: optimizable.map(mapQuestionToSDL),
  };
}

/** SDL JSON for every primer question — same shape as legacy “Download All” / handleDownloadAllSDL. */
export function buildAllQuestionsSDLPayload(questions: PrimerQuestion[], brandName: string, config?: PrimerQuestionConfig | null): SDLPayload {
  return {
    brand: brandName,
    industry: '',
    last_updated: new Date().toISOString().split('T')[0],
    competitors: [],
    ...(config?.strategic_foci ? { strategic_foci: config.strategic_foci } : {}),
    ...(config?.variables ? { variables: config.variables } : {}),
    questions: questions.map(mapQuestionToSDL),
  };
}

export function downloadSDLPayloadAsJson(payload: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
